import { Box, Stack, Typography } from "@mui/material";
import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import { Add as AddIcon } from "@mui/icons-material";
import AcxButton from "components/UI/AcxButton/AcxButton";
import { JsonSchemaObject } from "./types";

interface ObjectPropertiesEditorProps {
    schema: JsonSchemaObject;
    onRemoveProperty: (name: string) => void;
    onRenameProperty: (oldName: string) => void;
    onOpenAddDialog: () => void;
    isPropertyLocked?: (name: string) => boolean;
}

export function ObjectPropertiesEditor({
    schema,
    onRemoveProperty,
    onRenameProperty,
    onOpenAddDialog,
    isPropertyLocked = () => false,
}: ObjectPropertiesEditorProps) {
    const properties = schema.properties || {};
    const propertyCount = Object.keys(properties).length;

    return (
        <Box>
            <Typography variant="h6" mt={3} mb={1}>
                Properties
            </Typography>
            {propertyCount > 0 ? (
                <Stack direction="column" spacing={1} mb={2}>
                    {Object.entries(properties).map(
                        ([propName, propSchema]) => {
                            const isLocked = isPropertyLocked(propName);
                            return (
                                <Stack
                                    key={propName}
                                    direction="row"
                                    paddingBlock={1}
                                    paddingInline={2}
                                    justifyContent="space-between"
                                    alignItems="center"
                                    borderRadius={1}
                                    sx={(theme) => ({
                                        backgroundColor:
                                            theme.palette.lightgray.main,
                                    })}
                                >
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 500 }}
                                        >
                                            {propName}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                        >
                                            {propSchema.title || propSchema.type}
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={1}>
                                        <AcxButton
                                            size="small"
                                            variant="text"
                                            startIcon={<EditIcon />}
                                            onClick={() =>
                                                onRenameProperty(propName)
                                            }
                                            disabled={isLocked}
                                        >
                                            Rename
                                        </AcxButton>
                                        <AcxButton
                                            size="small"
                                            variant="text"
                                            color="error"
                                            startIcon={<DeleteIcon />}
                                            onClick={() =>
                                                onRemoveProperty(propName)
                                            }
                                            disabled={isLocked}
                                        >
                                            Remove
                                        </AcxButton>
                                    </Stack>
                                </Stack>
                            );
                        },
                    )}
                </Stack>
            ) : (
                <Typography variant="body2" color="textSecondary" mb={2}>
                    No properties yet
                </Typography>
            )}
            <AcxButton
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={onOpenAddDialog}
                fullWidth
            >
                Add Property
            </AcxButton>
        </Box>
    );
}
